import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Plus, Settings } from 'lucide-react'
import {
  MATERIAL_CERTIFICATE_TYPES,
  MATERIAL_CERTIFICATE_TYPE_LABELS,
  erfuelltRecht,
  type MaterialCertificateType,
} from '@symphenia/shared-types'
import { FilterBar } from '@/components/list/FilterBar'
import { ExportButton } from '@/components/ui/ExportButton'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { api } from '@/lib/axios'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import type { ExportRequest } from '@/lib/export-request'
import { inlineInput, pageWide } from '@/lib/form-styles'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import {
  FeldStatus,
  FeldStatusZeichen,
  Feldgruppe,
  feldKlasse,
  mitStand,
  useFeld,
  useSpeicherstelle,
  type Datensatz,
  type Feld,
  type Speicherstelle,
  type Status,
} from '@/lib/speichern'
import {
  createCertificate,
  deleteCertificate,
  fetchCertificates,
  oeffneZeugnis,
  updateCertificate,
  type CertificateFilter,
  type MaterialCertificate,
} from '../api/project-tech.api'
import { alsTag } from '../project-labels'
import { assignCertificate, linesForCertificate, type BomLine } from '../api/project-bom.api'
import { bestaetige } from '@/lib/dialoge'

/**
 * AP-35 Detailblatt 5 7.3 — **Materialzertifikate.**
 *
 * Listen-Standard: FilterBar, konfigurierbare Spalten, Export mit Mailweg.
 * Die Schmelze ist **das** Suchfeld über alle Projekte — sie steht deshalb in
 * der Vorauswahl der Spalten und wird von der Suche mit erfasst.
 *
 * „Zuordnen" (7.2 a und c) schlägt offene Stücklistenzeilen mit Zeugnispflicht
 * vor — über die Bestellung, den Werkstoff oder frühere Zuordnungen derselben
 * Schmelze; es gibt ihn nur mit dem Baustein Beschaffung (Phase G).
 *
 * AP-36 Phase F: Anlegen und Bearbeiten sind getrennt. Das Anlegen braucht die
 * Datei und bleibt ein Dialog mit „Anlegen" (Fall 2); ein bestehendes Zeugnis
 * speichert feldweise über `lib/speichern` (Fall 1). Öffnen, Zuordnen,
 * Aktivieren und Löschen sind Handlungen und behalten ihren Knopf.
 */

const FILTER_FELDER: FilterFieldDef[] = [
  { key: 'q', label: 'Suche', type: 'text', category: 'core', operators: ['contains'] },
  {
    key: 'type',
    label: 'Art',
    type: 'select',
    category: 'core',
    operators: ['is'],
    options: MATERIAL_CERTIFICATE_TYPES.map((t) => ({ value: t, label: MATERIAL_CERTIFICATE_TYPE_LABELS[t] })),
  },
  { key: 'from', label: 'Datum von', type: 'date', category: 'core', operators: ['is'] },
  { key: 'to', label: 'Datum bis', type: 'date', category: 'core', operators: ['is'] },
  { key: 'includeInactive', label: 'auch deaktivierte', type: 'boolean', category: 'core', operators: ['is'] },
  { key: 'unassigned', label: 'ohne Zuordnung', type: 'boolean', category: 'core', operators: ['is'] },
]
const STANDARD_FILTER = ['q', 'type']

interface ColDef {
  key: string
  label: string
}

const ALLE_SPALTEN: ColDef[] = [
  { key: 'type', label: 'Art' },
  { key: 'issuer', label: 'Aussteller' },
  { key: 'issuedAt', label: 'Datum' },
  { key: 'heatNumber', label: 'Schmelze' },
  { key: 'material', label: 'Werkstoff' },
  { key: 'dimension', label: 'Abmessung' },
  { key: 'standard', label: 'Norm' },
  { key: 'note', label: 'Anmerkung' },
  { key: 'active', label: 'aktiv' },
  { key: 'assignments', label: 'Zuordnungen' },
]
const COLUMN_LAYOUT_KEY = 'columnbar_layout_material_certificates'
const STANDARD_SPALTEN = ['type', 'issuer', 'issuedAt', 'heatNumber', 'material', 'dimension', 'assignments']

interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
}

function ladeSpalten(): ColumnState {
  const g = loadUiSetting<Partial<ColumnState> | null>(COLUMN_LAYOUT_KEY, null)
  return g?.columnKeys?.length ? { columnKeys: g.columnKeys, columnWidths: g.columnWidths ?? {} } : { columnKeys: STANDARD_SPALTEN, columnWidths: {} }
}

function alsFilter(werte: Record<string, ActiveFilter>): CertificateFilter {
  const f: CertificateFilter = {}
  for (const [k, a] of Object.entries(werte)) {
    if (!a.value) continue
    if (k === 'q') f.q = a.value
    if (k === 'type') f.type = a.value
    if (k === 'from') f.from = a.value.slice(0, 10)
    if (k === 'to') f.to = a.value.slice(0, 10)
    if (k === 'includeInactive') f.includeInactive = a.value === 'true'
    if (k === 'unassigned') f.unassigned = a.value === 'true'
  }
  return f
}

export default function MaterialCertificatesPage() {
  const { t } = useTranslation()
  const { user, hasModule } = useAuthStore()
  const darfPflegen = erfuelltRecht('projects.certificate.manage', user?.permissions)

  const [filterWerte, setFilterWerte] = useState<Record<string, ActiveFilter>>({})
  const [spalten, setSpalten] = useState<ColumnState>(ladeSpalten)
  const [konfig, setKonfig] = useState(false)
  const [zeilen, setZeilen] = useState<MaterialCertificate[]>([])
  const [gesamt, setGesamt] = useState(0)
  const [laedt, setLaedt] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [neu, setNeu] = useState(false)
  const [zuordnen, setZuordnen] = useState<MaterialCertificate | null>(null)
  const [bearbeiten, setBearbeiten] = useState<MaterialCertificate | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const laden = useCallback(async (werte: Record<string, ActiveFilter>) => {
    setLaedt(true)
    setFehler(null)
    try {
      const r = await fetchCertificates(alsFilter(werte))
      setZeilen(r.data)
      setGesamt(r.total)
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Zeugnisse konnten nicht geladen werden.'))
    } finally {
      setLaedt(false)
    }
  }, [])

  useEffect(() => {
    void laden({})
  }, [laden])

  const filterAendern = useCallback(
    (werte: Record<string, ActiveFilter>) => {
      setFilterWerte(werte)
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => void laden(werte), 400)
    },
    [laden],
  )

  const spaltenAendern = (fn: (p: ColumnState) => ColumnState) =>
    setSpalten((p) => {
      const n = fn(p)
      saveUiSetting(COLUMN_LAYOUT_KEY, n)
      return n
    })

  const gezeigt = spalten.columnKeys.map((k) => ALLE_SPALTEN.find((c) => c.key === k)).filter((c): c is ColDef => !!c)
  const hinzufuegbar = ALLE_SPALTEN.filter((c) => !spalten.columnKeys.includes(c.key))

  const zelle = (z: MaterialCertificate, key: string): React.ReactNode => {
    switch (key) {
      case 'type':
        return MATERIAL_CERTIFICATE_TYPE_LABELS[z.type] ?? z.type
      case 'issuer':
        return z.issuer ?? '—'
      case 'issuedAt':
        return alsTag(z.issuedAt)
      case 'active':
        return z.isActive ? 'ja' : 'nein'
      case 'assignments':
        return z.assignments.length
          ? [...new Set(z.assignments.map((a) => `${a.projectNumber}/${a.positionNumber}`))].join(', ')
          : '—'
      default:
        return ((z as unknown as Record<string, unknown>)[key] as string | null) ?? '—'
    }
  }

  async function tu(aktion: () => Promise<unknown>) {
    try {
      await aktion()
      await laden(filterWerte)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const exportAnfrage = (format: string, orientation?: string): ExportRequest => ({
    url: '/material-certificates/export',
    params: {
      ...alsFilter(filterWerte),
      format,
      orientation,
      columns: JSON.stringify([{ key: 'number', label: 'Nummer' }, ...gezeigt]),
      locale: navigator.language,
    },
  })

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Materialzertifikate</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Werkszeugnisse und Konformitätserklärungen — gesucht wird auch über Schmelze, Werkstoff und Aussteller.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            request={(format, orientation) => exportAnfrage(format, orientation)}
            mail={{
              source: 'EXPORT_MATERIAL_CERTIFICATES',
              title: t('mailExport.titles.EXPORT_MATERIAL_CERTIFICATES'),
              subject: t('mailExport.titles.EXPORT_MATERIAL_CERTIFICATES'),
            }}
          />
          {darfPflegen && (
            <button
              type="button"
              onClick={() => setNeu(true)}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground"
            >
              <Plus size={16} /> Zeugnis
            </button>
          )}
        </div>
      </div>

      <FilterBar
        availableFields={FILTER_FELDER}
        value={filterWerte}
        onChange={filterAendern}
        layoutKey="material-certificates"
        defaultFieldKeys={STANDARD_FILTER}
      />

      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setKonfig((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
            konfig ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
          title="Spalten konfigurieren"
        >
          {konfig ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />

        {konfig && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={COLUMN_LAYOUT_KEY}
              currentValue={spalten}
              onReset={(wert) => {
                const n = (wert as ColumnState | null) ?? { columnKeys: STANDARD_SPALTEN, columnWidths: {} }
                markUiSettingApplied(COLUMN_LAYOUT_KEY, n)
                setSpalten(n)
              }}
            />
          </div>
        )}

        {fehler && <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="px-4 py-1.5 text-left font-medium text-foreground">Nummer</th>
                {gezeigt.map((c) => (
                  <th key={c.key} className="px-4 py-1.5 text-left font-medium text-foreground">
                    <span className="flex items-center gap-1">
                      {c.label}
                      {konfig && (
                        <button
                          type="button"
                          className="ml-1 text-muted-foreground hover:text-destructive"
                          title="Spalte entfernen"
                          onClick={() => spaltenAendern((p) => ({ ...p, columnKeys: p.columnKeys.filter((k) => k !== c.key) }))}
                        >
                          ✕
                        </button>
                      )}
                    </span>
                  </th>
                ))}
                {konfig && hinzufuegbar.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                      defaultValue=""
                      onChange={(e) => {
                        const k = e.target.value
                        if (!k) return
                        spaltenAendern((p) => ({ ...p, columnKeys: [...p.columnKeys, k] }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">+ Spalte</option>
                      {hinzufuegbar.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </th>
                )}
                <th className="w-40" />
              </tr>
            </thead>
            <tbody>
              {!laedt && zeilen.length === 0 && (
                <tr>
                  <td colSpan={gezeigt.length + 3} className="px-4 py-8 text-center text-muted-foreground">
                    Keine Zeugnisse gefunden.
                  </td>
                </tr>
              )}
              {zeilen.map((z, i) => (
                <tr
                  key={z.id}
                  className={`border-b border-border last:border-0 ${i % 2 === 0 ? 'bg-background' : 'bg-muted/20'} ${
                    z.isActive ? '' : 'opacity-60'
                  }`}
                >
                  <td className="px-4 py-1.5 font-medium">{z.number}</td>
                  {gezeigt.map((c) => (
                    <td key={c.key} className="whitespace-nowrap px-4 py-1.5">
                      {zelle(z, c.key)}
                    </td>
                  ))}
                  {konfig && hinzufuegbar.length > 0 && <td />}
                  <td className="whitespace-nowrap px-4 py-1.5 text-right text-xs">
                    <button type="button" className="text-primary hover:underline" onClick={() => void tu(() => oeffneZeugnis(z.id, z.file.originalName))}>
                      öffnen
                    </button>
                    {darfPflegen && hasModule('PROJECT_MGMT.PROCUREMENT') && z.isActive && (
                      <button type="button" className="ml-3 text-primary hover:underline" onClick={() => setZuordnen(z)}>
                        zuordnen
                      </button>
                    )}
                    {darfPflegen && (
                      <>
                        <button type="button" className="ml-3 text-primary hover:underline" onClick={() => setBearbeiten(z)}>
                          bearbeiten
                        </button>
                        <button
                          type="button"
                          className="ml-3 hover:underline"
                          onClick={() => void tu(() => updateCertificate(z.id, { isActive: !z.isActive }))}
                        >
                          {z.isActive ? 'deaktivieren' : 'aktivieren'}
                        </button>
                        <button
                          type="button"
                          className="ml-3 text-destructive hover:underline"
                          onClick={async () => {
                            if (await bestaetige(`Zeugnis ${z.number} samt Datei löschen?`)) void tu(() => deleteCertificate(z.id))
                          }}
                        >
                          löschen
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {zeilen.length > 0 && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td className="px-4 py-1.5 text-muted-foreground" colSpan={gezeigt.length + 2}>
                    {laedt ? 'Laden…' : `${gesamt} Zeugnisse${gesamt > zeilen.length ? ` (die ersten ${zeilen.length} gezeigt)` : ''}`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {zuordnen && (
        <ZuordnenDialog
          zeugnis={zuordnen}
          onClose={() => setZuordnen(null)}
          onDone={() => {
            setZuordnen(null)
            void laden(filterWerte)
          }}
        />
      )}

      {neu && (
        <ZeugnisAnlegen
          onClose={() => setNeu(false)}
          onSaved={() => {
            setNeu(false)
            void laden(filterWerte)
          }}
        />
      )}

      {bearbeiten && (
        <ZeugnisBearbeiten
          zeugnis={bearbeiten}
          onClose={() => setBearbeiten(null)}
          onGespeichert={() => void laden(filterWerte)}
        />
      )}
    </div>
  )
}

/** Die beschreibenden Felder des Zeugnisses; die Längen sind die des DTO. */
const ZEUGNIS_FELDER: [key: string, label: string, max: number][] = [
  ['heatNumber', 'Schmelze / Charge', 60],
  ['material', 'Werkstoff', 60],
  ['dimension', 'Abmessung', 60],
  ['standard', 'Norm', 60],
  ['note', 'Anmerkung', 200],
]

/** Rahmen beider Zeugnis-Masken — gleiche Breite, gleiche Farben. */
function ZeugnisDialog({ titel, fuss, children }: { titel: string; fuss: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className="w-full max-w-xl space-y-3 rounded-lg border border-border p-5 shadow-xl"
        style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
      >
        <h2 className="text-lg font-semibold">{titel}</h2>
        {children}
        <div className="flex justify-end gap-2">{fuss}</div>
      </div>
    </div>
  )
}

/**
 * Lieferantensuche für das Ausstellerfeld. Der `setTimeout` entprellt die
 * **Suche** — gespeichert wird hier nichts.
 */
function useLieferanten(suche: string) {
  const [lieferanten, setLieferanten] = useState<{ id: string; name: string }[]>([])
  useEffect(() => {
    const t = setTimeout(() => {
      if (suche.trim().length < 2) return setLieferanten([])
      api
        .get<{ data: { id: string; name: string }[] }>('/clients', { params: { search: suche, limit: 10, role: 'supplier' } })
        .then((r) => setLieferanten(r.data.data ?? []))
        .catch(() => setLieferanten([]))
    }, 300)
    return () => clearTimeout(t)
  }, [suche])
  return lieferanten
}

function LieferantenVorschlaege({ eintraege, onWahl }: { eintraege: { id: string; name: string }[]; onWahl: (l: { id: string; name: string }) => void }) {
  if (eintraege.length === 0) return null
  return (
    <span className="mt-1 block max-h-32 overflow-auto rounded border border-border" style={{ background: 'hsl(var(--card))' }}>
      {eintraege.map((l) => (
        <button key={l.id} type="button" className="block w-full px-2 py-1 text-left text-sm text-foreground hover:bg-muted" onClick={() => onWahl(l)}>
          {l.name}
        </button>
      ))}
    </span>
  )
}

/**
 * Anlegen (Fall 2): der Dialog braucht die Datei — sie ist danach fest, ein
 * anderes Dokument ist ein anderes Zeugnis. Die beschreibenden Angaben stehen
 * mit dabei, weil sie beim Anlegen vom Dokument abgeschrieben werden; ändern
 * lassen sie sich danach feldweise.
 */
function ZeugnisAnlegen({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [datei, setDatei] = useState<File | null>(null)
  const [felder, setFelder] = useState<Record<string, string>>({ type: 'EN10204_3_1', issuedAt: new Date().toISOString().slice(0, 10) })
  const [suche, setSuche] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const lieferanten = useLieferanten(suche)

  const setze = (k: string, v: string) => setFelder((f) => ({ ...f, [k]: v }))

  async function anlegen() {
    if (!datei) return setFehler('Ein Zeugnis braucht die Datei.')
    try {
      await createCertificate(datei, felder)
      onSaved()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const feld = (k: string, label: string, max: number, pflicht = false) => (
    <label className="block text-xs text-muted-foreground">
      {label}
      {pflicht && ' *'}
      <input className={inlineInput} maxLength={max} value={felder[k] ?? ''} onChange={(e) => setze(k, e.target.value)} />
    </label>
  )

  return (
    <ZeugnisDialog
      titel="Neues Materialzertifikat"
      fuss={
        <>
          <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground" onClick={() => void anlegen()}>
            Anlegen
          </button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-xs text-muted-foreground">
          Art *
          <select className={inlineInput} value={felder.type} onChange={(e) => setze('type', e.target.value)}>
            {MATERIAL_CERTIFICATE_TYPES.map((t) => (
              <option key={t} value={t}>
                {MATERIAL_CERTIFICATE_TYPE_LABELS[t as MaterialCertificateType]}
              </option>
            ))}
          </select>
        </label>
        {feld('number', 'Zeugnisnummer', 60, true)}
        <label className="block text-xs text-muted-foreground">
          Ausgestellt am *
          <input type="date" className={inlineInput} value={felder.issuedAt} onChange={(e) => setze('issuedAt', e.target.value)} />
        </label>
        <label className="block text-xs text-muted-foreground">
          Lieferant (Partner)
          <input
            className={inlineInput}
            value={suche}
            placeholder="suchen …"
            onChange={(e) => {
              setSuche(e.target.value)
              setze('issuerClientId', '')
            }}
          />
          {!felder.issuerClientId && (
            <LieferantenVorschlaege
              eintraege={lieferanten}
              onWahl={(l) => {
                setze('issuerClientId', l.id)
                setSuche(l.name)
              }}
            />
          )}
        </label>
        {!felder.issuerClientId && feld('issuerName', 'oder Aussteller (Werk, frei)', 120)}
        {ZEUGNIS_FELDER.map(([k, label, max]) => (
          <div key={k}>{feld(k, label, max)}</div>
        ))}
      </div>
      <label className="block text-xs text-muted-foreground">
        Datei (PDF oder Bild) *
        <input type="file" accept="application/pdf,image/*" className="mt-1 block text-sm" onChange={(e) => setDatei(e.target.files?.[0] ?? null)} />
      </label>
      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
    </ZeugnisDialog>
  )
}

/**
 * Beschriftung, Feld, Zustand: das Häkchen steht neben dem Feld, der Fehler
 * darunter (AP-36 3.5) — sonst schöbe das Zeichen die Eingabe aus der Zeile.
 */
function FeldHuelle({ label, status, children }: { label: string; status: Status; children: React.ReactNode }) {
  return (
    <label className="block text-xs text-muted-foreground">
      {label}
      <span className="flex items-center gap-1.5">
        <span className="min-w-0 flex-1">{children}</span>
        <FeldStatusZeichen status={status} />
      </span>
      <FeldStatus status={status} zeichen={false} />
    </label>
  )
}

function TextFeld({ feld, label, max }: { feld: Feld<string | null>; label: string; max: number }) {
  return (
    <FeldHuelle label={label} status={feld}>
      <input className={feldKlasse} maxLength={max} {...feld.props} />
    </FeldHuelle>
  )
}

/**
 * Aussteller: entweder ein Partner oder ein freier Name — der Server verlangt
 * eines von beidem und prüft es **zusammen**. Also eine Gruppe (AP-36 3.1); die
 * Wahl eines Partners löscht den freien Namen im selben PATCH (Folgefeld).
 *
 * Die Hooks laufen in dieser Komponente, weil sie den Kontext der umgebenden
 * `Feldgruppe` brauchen.
 */
function AusstellerFelder({ stelle, anfangsname }: { stelle: Speicherstelle<unknown>; anfangsname: string }) {
  const partner = useFeld(stelle, 'issuerClientId', { label: 'Lieferant', folge: (w) => (w ? { issuerName: null } : {}) })
  const name = useFeld(stelle, 'issuerName', { label: 'Aussteller' })
  const [suche, setSuche] = useState(anfangsname)
  const lieferanten = useLieferanten(suche)

  return (
    <>
      <label className="block text-xs text-muted-foreground">
        Lieferant (Partner)
        <input
          className={feldKlasse}
          value={suche}
          placeholder="suchen …"
          onChange={(e) => {
            setSuche(e.target.value)
            // Wer weitersucht, hat den gewählten Partner verworfen; gesendet wird
            // das erst beim Verlassen der Gruppe, zusammen mit dem freien Namen.
            partner.tippe('')
          }}
        />
        {!partner.wert && (
          <LieferantenVorschlaege
            eintraege={lieferanten}
            onWahl={(l) => {
              setSuche(l.name)
              partner.setze(l.id)
            }}
          />
        )}
      </label>
      {!partner.wert && <TextFeld feld={name} label="oder Aussteller (Werk, frei)" max={120} />}
    </>
  )
}

/**
 * Ein bestehendes Zeugnis ändern — AP-36 Fall 1: Jedes Feld speichert beim
 * Verlassen, es gibt keinen Speichern-Knopf. Die Datei bleibt fest.
 */
function ZeugnisBearbeiten({ zeugnis, onClose, onGespeichert }: { zeugnis: MaterialCertificate; onClose: () => void; onGespeichert: () => void }) {
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `materialCertificate:${zeugnis.id}`,
    bezeichnung: `Zeugnis ${zeugnis.number}`,
    daten: zeugnis as unknown as Datensatz,
    schreibe: async (patch, stand) => {
      const r = await api.patch<{ data: Datensatz }>(`/material-certificates/${zeugnis.id}`, patch, mitStand(stand))
      // Die Liste dahinter zeigt dieselben Angaben — nach dem Schreiben ist sie veraltet.
      onGespeichert()
      return r.data
    },
    keepalive: { url: `/material-certificates/${zeugnis.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>

  const art = useFeld(stelle, 'type', { art: 'auswahl', label: 'Art' })
  const nummer = useFeld(stelle, 'number', { label: 'Zeugnisnummer', pflicht: true })
  const datum = useFeld(stelle, 'issuedAt', { art: 'datum', label: 'Ausgestellt am', pflicht: true })
  const schmelze = useFeld(stelle, 'heatNumber', { label: 'Schmelze / Charge' })
  const werkstoff = useFeld(stelle, 'material', { label: 'Werkstoff' })
  const abmessung = useFeld(stelle, 'dimension', { label: 'Abmessung' })
  const norm = useFeld(stelle, 'standard', { label: 'Norm' })
  const anmerkung = useFeld(stelle, 'note', { label: 'Anmerkung' })

  return (
    <ZeugnisDialog
      titel={`Zeugnis ${zeugnis.number} bearbeiten`}
      fuss={
        <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm" onClick={onClose}>
          Schließen
        </button>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <FeldHuelle label="Art" status={art}>
          <select className={feldKlasse} {...art.props}>
            {MATERIAL_CERTIFICATE_TYPES.map((t) => (
              <option key={t} value={t}>
                {MATERIAL_CERTIFICATE_TYPE_LABELS[t as MaterialCertificateType]}
              </option>
            ))}
          </select>
        </FeldHuelle>
        <TextFeld feld={nummer} label="Zeugnisnummer" max={60} />
        <FeldHuelle label="Ausgestellt am" status={datum}>
          <input type="date" className={feldKlasse} {...datum.props} />
        </FeldHuelle>
        <Feldgruppe
          stelle={stelle}
          label="Aussteller"
          pruefe={(w) => (!w.issuerClientId && !String(w.issuerName ?? '').trim() ? 'Ein Partner oder ein freier Name muss dastehen.' : null)}
          // `contents`: Die beiden Felder bleiben Zellen des Rasters — eine eigene
          // Hülle setzte sie in eine eigene Zeile und risse ein Loch daneben.
          className="contents"
          status={(s) => <FeldStatus status={s} zeichen={false} />}
        >
          <AusstellerFelder stelle={stelle} anfangsname={zeugnis.issuerClientId ? (zeugnis.issuer ?? '') : ''} />
        </Feldgruppe>
        <TextFeld feld={schmelze} label="Schmelze / Charge" max={60} />
        <TextFeld feld={werkstoff} label="Werkstoff" max={60} />
        <TextFeld feld={abmessung} label="Abmessung" max={60} />
        <TextFeld feld={norm} label="Norm" max={60} />
        <TextFeld feld={anmerkung} label="Anmerkung" max={200} />
      </div>
      <p className="text-xs text-muted-foreground">Datei: {zeugnis.file.originalName} — bleibt; ein anderes Dokument ist ein neues Zeugnis.</p>
    </ZeugnisDialog>
  )
}

/**
 * Blatt 5 7.2 a und c — ein Zeugnis Zeilen zuordnen: Vorschläge aus der
 * Bestellung, von der das Zeugnis kam, aus gleichem Werkstoff oder früheren
 * Zuordnungen derselben Schmelze. Mit einem Klick für alle markierten.
 */
function ZuordnenDialog({ zeugnis, onClose, onDone }: { zeugnis: MaterialCertificate; onClose: () => void; onDone: () => void }) {
  const [zeilen, setZeilen] = useState<(BomLine & { reason: string })[] | null>(null)
  const [haken, setHaken] = useState<Set<string>>(new Set())
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    linesForCertificate(zeugnis.id)
      .then((z) => {
        setZeilen(z)
        setHaken(new Set(z.map((x) => x.id)))
      })
      .catch((e) => setFehler(apiErrorText(e, 'Die Vorschläge ließen sich nicht laden.')))
  }, [zeugnis.id])

  const GRUND: Record<string, string> = { ORDER: 'aus der Bestellung', MATERIAL: 'gleicher Werkstoff', SUPPLIER: 'vom Aussteller bestellt' }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[85vh] w-full max-w-2xl space-y-3 overflow-auto rounded-lg border border-border p-5 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <h2 className="text-lg font-semibold">
          {zeugnis.number} zuordnen
          {zeugnis.heatNumber && <span className="ml-2 text-sm font-normal text-muted-foreground">Schmelze {zeugnis.heatNumber}</span>}
        </h2>
        {zeilen === null && !fehler && <p className="text-sm text-muted-foreground">Lädt …</p>}
        {zeilen?.length === 0 && (
          <p className="text-sm text-muted-foreground">Keine offene Stücklistenzeile mit Zeugnispflicht passt — Zuordnen geht auch aus der Stückliste heraus.</p>
        )}
        <ul className="space-y-1 text-sm">
          {zeilen?.map((z) => (
            <li key={z.id} className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={haken.has(z.id)}
                onChange={() =>
                  setHaken((h) => {
                    const n = new Set(h)
                    if (n.has(z.id)) n.delete(z.id)
                    else n.add(z.id)
                    return n
                  })
                }
                aria-label={z.name}
              />
              <span>
                <span className="font-medium">{z.name}</span> · {z.project.projectNumber} / {z.position.label}
                <span className="block text-xs text-muted-foreground">{[z.material, z.supplier?.name, GRUND[z.reason]].filter(Boolean).join(' · ')}</span>
              </span>
            </li>
          ))}
        </ul>
        {fehler && <p className="text-sm text-destructive">{fehler}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm" onClick={onClose}>
            Schließen
          </button>
          <button
            type="button"
            disabled={haken.size === 0}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
            onClick={() =>
              void assignCertificate(zeugnis.id, [...haken])
                .then(onDone)
                .catch((e) => setFehler(apiErrorText(e, 'Das hat nicht geklappt.')))
            }
          >
            {haken.size} Zeilen zuordnen
          </button>
        </div>
      </div>
    </div>
  )
}
